import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2 } from 'lucide-react';

import useMyPreboardingApi from './config';
import Tabs from '../../components/ui/Tabs';
import OverviewTab from './tab-pages/OverviewTab';
import TasksTab from './tab-pages/TasksTab';
import DocumentsTab from './tab-pages/DocumentsTab';
import WelcomeContentTab from './tab-pages/WelcomeContentTab';

const Page = styled.div`
  padding: 8px 4px 40px;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 0;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const EmptyIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const EmptyTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

export default function MyPreboardingPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const {
    loading, home, tasks, documents, welcomeContent,
    startTask, completeTask, uploadDocument, markContentViewed,
  } = useMyPreboardingApi();

  const tabs = [
    { key: 'overview', label: t('myPreboarding.tab.overview') },
    { key: 'tasks', label: t('myPreboarding.tab.tasks') },
    { key: 'documents', label: t('myPreboarding.tab.documents') },
    { key: 'welcome', label: t('myPreboarding.tab.welcome') },
  ];

  return (
    <Page>
      <Header>
        <Title>{t('myPreboarding.title')}</Title>
        <Subtitle>{t('myPreboarding.subtitle')}</Subtitle>
      </Header>

      {loading && (
        <Centered>
          <Spinner size={32} />
        </Centered>
      )}

      {!loading && !home && (
        <Centered>
          <EmptyIconWrap>
            <Sparkles size={28} />
          </EmptyIconWrap>
          <EmptyTitle>{t('myPreboarding.empty.title')}</EmptyTitle>
          <EmptyText>{t('myPreboarding.empty.text')}</EmptyText>
        </Centered>
      )}

      {!loading && home && (
        <>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === 'overview' && (
            <OverviewTab home={home} onGoTab={setActiveTab} />
          )}
          {activeTab === 'tasks' && (
            <TasksTab
              tasks={tasks}
              onStart={startTask}
              onComplete={completeTask}
              onGoDocuments={() => setActiveTab('documents')}
            />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab documents={documents} onUpload={uploadDocument} />
          )}
          {activeTab === 'welcome' && (
            <WelcomeContentTab items={welcomeContent} onMarkViewed={markContentViewed} />
          )}
        </>
      )}
    </Page>
  );
}
